import React, { useEffect, useRef, useState } from 'react';
import { Button, Tooltip, Typography, Upload } from 'antd';
import {
  AudioOutlined,
  AudioMutedOutlined,
  BookOutlined,
  CheckCircleFilled,
  CustomerServiceOutlined,
  SoundOutlined,
  CloseCircleOutlined,
  FileOutlined,
  GlobalOutlined,
  PaperClipOutlined,
  SendOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import {
  MAX_TEAM_ASSISTANT_COMPOSER_ATTACHMENTS,
  TEAM_ASSISTANT_UPLOAD_ACCEPT,
} from '../../utils/teamAssistantUploadAccept';

const { Text } = Typography;

const TeamAssistantComposer = ({
  me,
  draft,
  onDraftChange,
  attachments = [],
  removeAttachment,
  hasUploadingAttachments = false,
  readyAttachments = [],
  listening,
  streaming,
  useWebSearch,
  setUseWebSearch,
  onSend,
  onVoiceToggle,
  onAttachUpload,
  onStopStream,
  voiceMode = false,
  onVoiceModeToggle,
  speaking = false,
  onStopSpeaking,
  voiceSupported = true,
  compact = false,
  features = {},
}) => {
  const {
    upload = true,
    voice = true,
    voiceChatMode = true,
    webSearch = true,
    catalogTags = true,
    footnote = true,
    singleLineComposer = false,
  } = features;

  const [composerExpanded, setComposerExpanded] = useState(false);
  const [localDraft, setLocalDraft] = useState('');
  const emptyExpandedRef = useRef(false);
  const isSlim = singleLineComposer && !composerExpanded;

  const hasAttachments = attachments.length > 0;
  const hasReadyAttachments = readyAttachments.length > 0;
  const indexedReadyCount = readyAttachments.filter((a) => !a.usesVision).length;
  const visionReadyCount = readyAttachments.filter((a) => a.usesVision).length;
  const atAttachmentLimit = attachments.length >= MAX_TEAM_ASSISTANT_COMPOSER_ATTACHMENTS;

  const canSend =
    !streaming &&
    !hasUploadingAttachments &&
    (Boolean((localDraft || draft || '').trim()) || hasReadyAttachments);

  const updateDraft = (value) => {
    setLocalDraft(value);
    onDraftChange?.(value);
  };

  useEffect(() => {
    if (listening) {
      setLocalDraft(draft ?? '');
    }
  }, [draft, listening]);

  useEffect(() => {
    if (listening) return;
    setLocalDraft(draft ?? '');
  }, [draft, listening]);

  const collapseComposer = () => {
    emptyExpandedRef.current = false;
    setComposerExpanded(false);
  };

  useEffect(() => {
    if (singleLineComposer && localDraft.includes('\n')) {
      emptyExpandedRef.current = false;
      setComposerExpanded(true);
    }
  }, [localDraft, singleLineComposer]);

  useEffect(() => {
    if (!singleLineComposer || !composerExpanded || listening) return;
    const trimmed = (localDraft || '').trim();
    if (trimmed) {
      emptyExpandedRef.current = false;
      return;
    }
    if (!emptyExpandedRef.current) {
      setComposerExpanded(false);
    }
  }, [localDraft, listening, singleLineComposer, composerExpanded]);

  useEffect(() => {
    if (listening) {
      setComposerExpanded(true);
    }
  }, [listening]);

  useEffect(() => {
    if (hasAttachments && singleLineComposer) {
      setComposerExpanded(true);
    }
  }, [hasAttachments, singleLineComposer]);

  const showVoiceToast = Boolean(listening || speaking);

  const handleSend = () => {
    if (!canSend) return;
    onSend();
    setLocalDraft('');
    onDraftChange?.('');
    if (singleLineComposer) {
      collapseComposer();
    }
  };

  const showSlimInlineSources = isSlim && catalogTags;
  const slimVoiceChatInToolbar = voiceChatMode && !(isSlim && compact);

  const placeholder = hasReadyAttachments
    ? readyAttachments.length > 1
      ? 'Ask about these files…'
      : 'Ask about this file…'
    : isSlim
      ? compact
        ? 'Message…'
        : `Message as ${me.name}…`
      : `Message as ${me.name}… Shift+Enter for a new line`;

  const composerHint = isSlim
    ? compact
      ? `Message as ${me.name}. Ctrl+Enter for more lines.`
      : `Ctrl+Enter for more lines`
    : `Shift+Enter for a new line`;

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!(localDraft || '').trim()) {
        emptyExpandedRef.current = true;
      }
      setComposerExpanded(true);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }
    if (e.key === 'Escape' && singleLineComposer && composerExpanded) {
      collapseComposer();
    }
  };

  const textareaRows = isSlim ? 1 : singleLineComposer && composerExpanded ? 3 : compact ? 2 : 2;
  const textareaMaxHeight = isSlim
    ? undefined
    : singleLineComposer
      ? compact
        ? 168
        : 280
      : compact
        ? 140
        : 200;

  const cardClass = [
    'team-assistant-composer-card',
    streaming ? 'team-assistant-composer-card-busy' : '',
    singleLineComposer ? 'team-assistant-composer-card--slim' : '',
    composerExpanded ? 'is-expanded' : '',
    hasAttachments ? 'has-attachment has-attachments' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const chipClass = (extra = '') =>
    `team-assistant-source-chip ${extra}`.trim();

  const beforeUpload = (file, fileList) => {
    const batchIndex = fileList.findIndex((f) => f.uid === file.uid);
    const room = MAX_TEAM_ASSISTANT_COMPOSER_ATTACHMENTS - attachments.length;
    if (room <= 0 || batchIndex >= room) {
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const renderAttachmentStrip = () => {
    if (!hasAttachments) return null;

    return (
      <div className="team-assistant-composer-attachments" aria-live="polite">
        {attachments.map((item) => {
          const uploading = item.status === 'uploading';
          const failed = item.status === 'error';
          return (
            <div
              key={item.clientId}
              className={[
                'team-assistant-composer-attachment-inline',
                uploading ? 'is-uploading' : '',
                failed ? 'is-error' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <FileOutlined aria-hidden />
              <span className="team-assistant-composer-attachment-name" title={item.name}>
                {item.name}
              </span>
              {uploading && (
                <span className="team-assistant-composer-attachment-progress" aria-hidden>
                  <span
                    className="team-assistant-composer-attachment-progress-bar"
                    style={{ width: `${item.progress || 0}%` }}
                  />
                </span>
              )}
              {uploading && (
                <span className="team-assistant-composer-attachment-pct">{item.progress || 0}%</span>
              )}
              {failed && (
                <span className="team-assistant-composer-attachment-err" title={item.errorMessage}>
                  Failed
                </span>
              )}
              <button
                type="button"
                className="team-assistant-composer-attachment-remove"
                onClick={() => removeAttachment?.(item.clientId)}
                aria-label={`Remove ${item.name}`}
              >
                <CloseCircleOutlined />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAttachSummary = () => {
    if (!hasReadyAttachments && !hasUploadingAttachments) return null;
    const parts = [];
    if (readyAttachments.length) {
      parts.push(`${readyAttachments.length} ready`);
    }
    if (indexedReadyCount) {
      parts.push(`${indexedReadyCount} indexed`);
    }
    if (visionReadyCount) {
      parts.push(`${visionReadyCount} image${visionReadyCount > 1 ? 's' : ''} on send`);
    }
    if (hasUploadingAttachments) {
      parts.push('uploading…');
    }
    return (
      <div className="team-assistant-composer-attach-summary" aria-live="polite">
        {parts.join(' · ')}
      </div>
    );
  };

  const renderComposerTools = (inFooter = false) => (
    <div
      className={`team-assistant-composer-tools ${
        inFooter ? 'team-assistant-composer-tools--footer' : ''
      }`}
      role="toolbar"
      aria-label="Message tools"
    >
      {upload && (
        <Tooltip
          title={
            atAttachmentLimit
              ? `Maximum ${MAX_TEAM_ASSISTANT_COMPOSER_ATTACHMENTS} files`
              : 'Attach PDF, Office docs, or images (multiple files OK)'
          }
        >
          <Upload
            customRequest={onAttachUpload}
            showUploadList={false}
            multiple
            disabled={streaming || atAttachmentLimit}
            accept={TEAM_ASSISTANT_UPLOAD_ACCEPT}
            beforeUpload={beforeUpload}
          >
            <Button
              type="text"
              className="team-assistant-tool-btn"
              icon={<PaperClipOutlined />}
              loading={hasUploadingAttachments}
              disabled={streaming || atAttachmentLimit}
            />
          </Upload>
        </Tooltip>
      )}
      {voice && slimVoiceChatInToolbar && voiceSupported && (
        <Tooltip
          title={
            voiceMode
              ? 'Voice chat on — auto-send & spoken replies. Click to turn off.'
              : 'Voice chat — speak questions, hear answers (Chrome / Edge / Safari)'
          }
        >
          <Button
            type="text"
            className={`team-assistant-tool-btn ${
              voiceMode ? 'team-assistant-tool-btn-active' : ''
            }`}
            icon={voiceMode ? <CustomerServiceOutlined /> : <SoundOutlined />}
            onClick={onVoiceModeToggle}
            disabled={streaming || listening}
            aria-pressed={voiceMode}
          />
        </Tooltip>
      )}
      {voice && (
        <Tooltip title={listening ? 'Stop voice input' : 'Voice input (you can still type while listening)'}>
          <Button
            type="text"
            className={`team-assistant-tool-btn ${listening ? 'team-assistant-tool-btn-active' : ''}`}
            danger={listening}
            icon={listening ? <AudioMutedOutlined /> : <AudioOutlined />}
            onClick={onVoiceToggle}
            disabled={streaming}
          />
        </Tooltip>
      )}
    </div>
  );

  const renderSourceChips = (iconOnly = false) => (
    <>
      {catalogTags && (
        <>
          <Tooltip title="Searches team file library on every message">
            <span
              className={chipClass(
                `team-assistant-source-chip--active ${
                  iconOnly ? 'team-assistant-source-chip--icon-only' : ''
                }`
              )}
            >
              <BookOutlined aria-hidden />
              {!iconOnly && <span>Files</span>}
              {!iconOnly && (
                <CheckCircleFilled className="team-assistant-source-check" aria-hidden />
              )}
            </span>
          </Tooltip>
          <Tooltip title="Market Finder companies, LOB, and states">
            <span
              className={chipClass(
                `team-assistant-source-chip--active ${
                  iconOnly ? 'team-assistant-source-chip--icon-only' : ''
                }`
              )}
            >
              <ShopOutlined aria-hidden />
              {!iconOnly && <span>Catalog</span>}
              {!iconOnly && (
                <CheckCircleFilled className="team-assistant-source-check" aria-hidden />
              )}
            </span>
          </Tooltip>
        </>
      )}
      {webSearch && (
        <Tooltip title={useWebSearch ? 'Web search on' : 'Enable web search'}>
          <button
            type="button"
            className={chipClass(
              `team-assistant-source-chip--toggle ${
                useWebSearch ? 'is-on' : ''
              } ${iconOnly ? 'team-assistant-source-chip--icon-only' : ''}`
            )}
            disabled={streaming}
            onClick={() => setUseWebSearch(!useWebSearch)}
            aria-pressed={useWebSearch}
            aria-label="Web search"
          >
            <GlobalOutlined aria-hidden />
            {!iconOnly && <span>Web</span>}
          </button>
        </Tooltip>
      )}
    </>
  );

  return (
    <div className="team-assistant-composer-wrap">
      {showVoiceToast && (
        <div className="team-assistant-composer-toast" aria-live="polite">
          {listening && (
            <span className="team-assistant-toast-item team-assistant-toast-item--voice">
              <span className="team-assistant-toast-pulse" aria-hidden />
              <span>{voiceMode ? 'Listening — tap mic to send' : 'Listening'}</span>
            </span>
          )}
          {speaking && (
            <span className="team-assistant-toast-item team-assistant-toast-item--voice">
              <span className="team-assistant-toast-pulse" aria-hidden />
              <span>Speaking</span>
              {onStopSpeaking && (
                <Button type="link" size="small" onClick={onStopSpeaking}>
                  Stop
                </Button>
              )}
            </span>
          )}
        </div>
      )}

      <div className={cardClass}>
        {renderAttachmentStrip()}
        {renderAttachSummary()}
        <div className="team-assistant-composer-input-row">
          {isSlim && renderComposerTools(false)}
          {isSlim && showSlimInlineSources && (
            <div
              className="team-assistant-composer-inline-sources"
              role="group"
              aria-label="Message sources"
            >
              {renderSourceChips(true)}
            </div>
          )}
          <textarea
            className={[
              'team-assistant-composer-field',
              isSlim ? 'team-assistant-composer-field--single-line' : '',
              composerExpanded ? 'team-assistant-composer-field--expanded' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            value={localDraft}
            onChange={(e) => updateDraft(e.target.value)}
            placeholder={placeholder}
            rows={textareaRows}
            style={textareaMaxHeight ? { maxHeight: textareaMaxHeight } : undefined}
            disabled={streaming}
            onKeyDown={handleKeyDown}
            spellCheck
            autoComplete="off"
            aria-label={composerHint}
            title={composerHint}
          />
          {isSlim && (
            <Button
              type="primary"
              className="team-assistant-send-btn team-assistant-send-btn--inline"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={streaming}
              disabled={!canSend}
              aria-label="Send"
            />
          )}
        </div>

        {!isSlim && (
        <div className="team-assistant-composer-footer">
          <div className="team-assistant-composer-footer-main">
            {renderComposerTools(true)}
            <div className="team-assistant-sources">
              <div className="team-assistant-source-chips">
                {renderSourceChips(false)}
              </div>
            </div>
          </div>
          <Button
            type="primary"
            size={compact ? 'middle' : 'large'}
            className="team-assistant-send-btn"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={streaming && !onStopStream}
            disabled={!canSend && !streaming}
          >
            {streaming ? 'Sending…' : 'Send'}
          </Button>
          {streaming && onStopStream && (
            <Button size={compact ? 'middle' : 'large'} onClick={onStopStream}>
              Stop
            </Button>
          )}
        </div>
        )}
      </div>
      {footnote && !compact && !singleLineComposer && (
        <Text type="secondary" className="team-assistant-composer-footnote">
          Enter to send · Attach multiple files · Type while uploads finish
        </Text>
      )}
    </div>
  );
};

export default TeamAssistantComposer;
